/**
 * Emulates the keyboard-wedge behaviour of a real Zebra DS2200 / ZKB203S
 * barcode scanner: rapidly types a string into a target input + dispatches
 * an `Enter` keydown.
 *
 * Real scanners write to whatever element currently has focus, so callers
 * pass in the target explicitly to avoid focus drift.
 */
const HID_INTER_CHAR_DELAY_MS = 8;

function setNativeValue(
  element: HTMLInputElement | HTMLTextAreaElement,
  value: string
) {
  const proto = Object.getPrototypeOf(element);
  const descriptor = Object.getOwnPropertyDescriptor(proto, 'value');
  const setter = descriptor?.set;
  if (setter) {
    setter.call(element, value);
  } else {
    element.value = value;
  }
}

export async function dispatchHidScan(
  target: HTMLInputElement | HTMLTextAreaElement,
  value: string,
  options: { trailingEnter?: boolean; perCharDelayMs?: number } = {}
): Promise<void> {
  const { trailingEnter = true, perCharDelayMs = HID_INTER_CHAR_DELAY_MS } =
    options;

  target.focus();
  setNativeValue(target, '');
  target.dispatchEvent(new Event('input', { bubbles: true }));

  let buffer = '';
  for (const ch of value) {
    buffer += ch;
    setNativeValue(target, buffer);
    target.dispatchEvent(
      new KeyboardEvent('keydown', { key: ch, bubbles: true })
    );
    target.dispatchEvent(new Event('input', { bubbles: true }));
    target.dispatchEvent(
      new KeyboardEvent('keyup', { key: ch, bubbles: true })
    );
    if (perCharDelayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, perCharDelayMs));
    }
  }

  target.dispatchEvent(new Event('change', { bubbles: true }));

  if (trailingEnter) {
    target.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Enter',
        code: 'Enter',
        bubbles: true
      })
    );
    target.dispatchEvent(
      new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', bubbles: true })
    );
  }
}

export function isTextLikeInput(
  element: Element | null
): element is HTMLInputElement | HTMLTextAreaElement {
  if (!element) return false;
  if (element instanceof HTMLTextAreaElement) return true;
  if (element instanceof HTMLInputElement) {
    const type = element.type.toLowerCase();
    return (
      type === 'text' ||
      type === 'search' ||
      type === 'number' ||
      type === 'tel' ||
      type === 'url' ||
      type === ''
    );
  }
  return false;
}
